import {
  DEFAULT_APP_SHORT_NAME,
  RESTAURANT_UUID_RE,
  waiterAppLabel,
  waiterManifestPath,
  waiterServiceWorkerPath,
} from './waiterManifest';

/*
 * SC-906 — ce qu'il faut poser dans le <head> pour qu'un navigateur accepte de
 * mettre le carnet sur l'ecran d'accueil, et de le rouvrir en plein ecran.
 *
 * Pose ici et pas dans l'index.html parce que l'index.html est partage avec le
 * site du client : le manifeste designe UN restaurant (son uuid est dans
 * `start_url`), et proposer « installer l'application » a un convive qui scanne
 * un QR de table n'a aucun sens. WaiterShell est la seule racine qui ne se
 * monte que sur la surface du personnel.
 *
 * Rien n'est retire au demontage : ces balises ne coutent rien une fois posees,
 * et le carnet quitte WaiterShell des qu'il ouvre une table (`/<uuid>/table/…`,
 * qui est dans la portee de l'application installee). Les retirer la ferait
 * clignoter l'etat « installable » au milieu du service.
 */

const MARKER = 'data-waiter-app';

function ensureLink(rel: string, href: string): void {
  const existing = document.head.querySelector<HTMLLinkElement>(`link[${MARKER}][rel="${rel}"]`);
  if (existing) {
    // Un meme appareil peut changer de restaurant sans recharger la page : le
    // manifeste doit alors designer le nouveau, pas l'ancien.
    if (existing.href !== new URL(href, document.baseURI).href) existing.href = href;
    return;
  }
  const link = document.createElement('link');
  link.setAttribute(MARKER, '');
  link.rel = rel;
  link.href = href;
  document.head.appendChild(link);
}

function ensureMeta(name: string, content: string): void {
  const existing = document.head.querySelector<HTMLMetaElement>(`meta[${MARKER}][name="${name}"]`);
  if (existing) {
    existing.content = content;
    return;
  }
  const meta = document.createElement('meta');
  meta.setAttribute(MARKER, '');
  meta.name = name;
  meta.content = content;
  document.head.appendChild(meta);
}

export function registerWaiterApp(restaurantUuid: string, restaurantName?: string | null): void {
  if (typeof document === 'undefined' || !RESTAURANT_UUID_RE.test(restaurantUuid)) return;

  ensureLink('manifest', waiterManifestPath(restaurantUuid, restaurantName));
  // iOS ne lit pas les icones du manifeste pour l'ecran d'accueil sur les
  // versions encore en service : sans apple-touch-icon, l'icone posee est une
  // capture de la page.
  ensureLink('apple-touch-icon', '/img/waiter-app-180.png');
  // L'index.html est partage avec le site du convive et ne porte donc aucune
  // icone : sans cette ligne, l'onglet du carnet reste sur l'icone par defaut du
  // navigateur. Elle n'est posee que sur la surface du personnel, la marque du
  // produit n'ayant rien a faire dans l'onglet d'un client (le convive, lui,
  // recoit l'icone du restaurant, posee par RestaurantShell).
  ensureLink('icon', '/img/waiter-app-32.png');
  // La paire qui, sur iPhone, fait s'ouvrir l'icone en plein ecran plutot que
  // dans un onglet Safari. `mobile-web-app-capable` est la forme standard,
  // `apple-mobile-web-app-capable` celle que Safari lit encore.
  ensureMeta('mobile-web-app-capable', 'yes');
  ensureMeta('apple-mobile-web-app-capable', 'yes');
  // Le titre iOS est ce qui s'ecrit SOUS l'icone : sans le nom du restaurant,
  // un serveur qui tourne sur deux etablissements pose deux icones identiques
  // et ne sait plus laquelle ouvre quelle salle.
  ensureMeta(
    'apple-mobile-web-app-title',
    waiterAppLabel(restaurantName) || DEFAULT_APP_SHORT_NAME,
  );
  ensureMeta('apple-mobile-web-app-status-bar-style', 'default');

  /*
   * Le worker est ce qui rend l'installation POSSIBLE (Chrome n'emet
   * `beforeinstallprompt` que si un worker couvre le `start_url`) ; il ne met
   * aucun fichier de l'application en cache — voir public/waiter-sw.js.
   *
   * `navigator.serviceWorker` n'existe pas hors contexte securise : en dev sur
   * une IP de LAN en http, il n'y a ni worker ni invitation a installer, et
   * c'est sans consequence pour le reste du carnet.
   */
  if (!('serviceWorker' in navigator)) return;
  void navigator.serviceWorker
    .register(waiterServiceWorkerPath(restaurantUuid), { scope: `/${restaurantUuid}/` })
    .catch(() => undefined);
}
